/**
 * crucible#3187 — `useSpeechFeedback`, extracted from `TvSteeringSurface.tsx`
 * (where it was defined inline as a private hook for the old
 * `PhoneSteeringDriver`) so `PhoneSteeringController.tsx` can use the SAME
 * hook without importing from `TvSteeringSurface.tsx` (which imports the
 * controller — a circular module dependency). Behavior is unchanged from the
 * original: browser SpeechRecognition, feeding transcripts to `onTranscript`,
 * mapping onerror codes to a friendly message via `voiceInputErrorMessage`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { voiceInputErrorMessage } from '../lib/voice-error.js';

type SpeechRecognitionAlternativeLike = {
  transcript: string;
};

type SpeechRecognitionResultLike = {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): SpeechRecognitionAlternativeLike;
};

type SpeechRecognitionResultListLike = {
  readonly length: number;
  item(index: number): SpeechRecognitionResultLike;
};

type SpeechRecognitionEventLike = {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultListLike;
};

type SpeechRecognitionErrorEventLike = {
  readonly error?: string;
  readonly message?: string;
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
};

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

type SpeechRecognitionWindow = Window &
  typeof globalThis & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };

function speechRecognitionCtor(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') return null;
  const speechWindow = window as SpeechRecognitionWindow;
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition ?? null;
}

export function useSpeechFeedback({
  active,
  onTranscript,
}: {
  active: boolean;
  onTranscript: (value: string) => void;
}): {
  supported: boolean;
  listening: boolean;
  error: string | null;
  start: () => void;
  stop: () => void;
} {
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const [supported] = useState(() => speechRecognitionCtor() !== null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    setListening(false);
  }, []);

  const start = useCallback(() => {
    const Recognition = speechRecognitionCtor();
    if (!Recognition) {
      setError('Voice unavailable. Type the change instead.');
      return;
    }
    recognitionRef.current?.abort();
    const recognition = new Recognition();
    recognitionRef.current = recognition;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      // #2996 Codex round 2 — a superseded instance's queued callbacks must
      // not touch shared state while a newer attempt is live.
      if (recognitionRef.current !== recognition) return;
      let transcript = '';
      // Web Speech may advance resultIndex after finalising an earlier phrase while
      // retaining that phrase in results. Consumers replace their current dictation
      // draft with this callback, so always emit the complete recognition-session
      // transcript instead of only the newly changed suffix.
      for (let i = 0; i < event.results.length; i += 1) {
        const result = event.results.item(i);
        if (result.length === 0) continue;
        transcript += result.item(0).transcript;
      }
      const next = transcript.trim();
      if (next) onTranscript(next);
    };
    recognition.onerror = (event) => {
      if (recognitionRef.current !== recognition) return;
      // #2992 sweep — map to a user-facing message (the raw error code, e.g.
      // "not-allowed", used to be rendered verbatim); benign outcomes
      // (no-speech, aborted) stay quiet.
      setError(voiceInputErrorMessage(event.error, 'type the change instead'));
      setListening(false);
    };
    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return;
      setListening(false);
    };
    try {
      recognition.start();
      setError(null);
      setListening(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Voice input did not start.');
      setListening(false);
    }
  }, [onTranscript]);

  useEffect(() => {
    if (active) return;
    recognitionRef.current?.abort();
    recognitionRef.current = null;
    setListening(false);
    setError(null);
  }, [active]);

  useEffect(
    () => () => {
      recognitionRef.current?.abort();
    },
    [],
  );

  return { supported, listening, error, start, stop };
}
